import { DecodePreview, DecodedInterval } from '@/api/workoutsService';

export type DecodeRole = 'WORK' | 'REC r' | 'REC R' | 'LOOP';

export interface DecodeRow {
  stepNo: number | null;
  ellipsis: boolean;
  duration: string;
  target: string;
  role: DecodeRole;
}

const isGroup = (node: DecodedInterval): boolean => node.children.length > 0;

export const formatClock = (sec: number | null): string => {
  if (sec === null) return '—';
  if (sec >= 60 && sec % 60 === 0) return `${sec / 60}′`;
  if (sec < 60) return `${sec}″`;
  return `${Math.floor(sec / 60)}′${sec % 60}`;
};

/** A leaf's magnitude: reps/Max for bodyweight, metres for swim distance leaves, else a clock. */
export const formatMeasure = (node: DecodedInterval): string => {
  if (node.isMax) return 'Max';
  if (node.reps !== null) return `${node.reps} rép.`;
  if (node.distanceM !== null) return `${node.distanceM} m`;
  return formatClock(node.durationSec);
};

export const formatMinutes = (sec: number): string => `${Math.round(sec / 60)} min`;

export const formatMetres = (m: number): string => `${m} m`;

/**
 * The critical intensity a percentage refers to, per discipline. The decoder accepts `CP` and `CS`
 * as tokens and never stores the unit, so it is read from the workout's sport rather than defaulted
 * to `CP` — which used to label a swim or a run « % CP ».
 */
const criticalLabelOf = (sportIdentifier?: string): string => {
  switch (sportIdentifier) {
    case 'SWIMMING':
      return 'CSS';
    case 'RUNNING':
      return 'CS';
    default:
      return 'CP';
  }
};

export const formatTarget = (node: DecodedInterval, sportIdentifier?: string): string => {
  const unit = criticalLabelOf(sportIdentifier);
  const parts: string[] = [];
  if (node.mode === 'ALL_OUT') {
    parts.push('All-out');
  } else if (node.intensityPctIcMin !== null || node.intensityPctIcMax !== null) {
    const min = node.intensityPctIcMin ?? node.intensityPctIcMax;
    const max = node.intensityPctIcMax ?? node.intensityPctIcMin;
    parts.push(min !== null && max !== null && min !== max ? `${min}–${max}% ${unit}` : `${min}% ${unit}`);
  } else if (node.zoneLabel) {
    parts.push(node.zoneLabel);
  }
  if (node.cadence !== null) parts.push(`${node.cadence} rpm`);
  if (node.note?.cue) parts.push(node.note.cue);
  if (node.note?.equipment.length) parts.push(`🛠 ${node.note.equipment.join(', ')}`);
  if (node.unilateral) parts.push('par membre');
  return parts.length > 0 ? parts.join(' · ') : '—';
};

const roleOf = (node: DecodedInterval, sectionCode: string | null): DecodeRole => {
  if (isGroup(node)) return 'LOOP';
  if (sectionCode === 'COOLDOWN') return 'REC R';
  if (node.isRecovery) return 'REC r';
  return 'WORK';
};

interface FlattenCtx {
  rows: DecodeRow[];
  step: { n: number };
  block: { n: number };
  sportIdentifier?: string;
}

const emitLeaf = (node: DecodedInterval, sectionCode: string | null, ctx: FlattenCtx) => {
  ctx.rows.push({
    stepNo: ctx.step.n,
    ellipsis: false,
    duration: formatMeasure(node),
    target: formatTarget(node, ctx.sportIdentifier),
    role: roleOf(node, sectionCode),
  });
  ctx.step.n += 1;
};

const emitLoopRow = (label: string, repeat: number, unit: string, ctx: FlattenCtx) => {
  ctx.rows.push({
    stepNo: null,
    ellipsis: true,
    duration: `× ${repeat}${unit}`,
    target: label,
    role: 'LOOP',
  });
};

const emitNode = (node: DecodedInterval, isOutermost: boolean, sectionCode: string | null, ctx: FlattenCtx) => {
  if (!isGroup(node)) {
    emitLeaf(node, sectionCode, ctx);
    return;
  }

  const leafOnly = node.children.every((c) => c.children.length === 0);
  if (leafOnly) {
    const sample = Math.min(node.repeatCount, 2);
    for (let i = 0; i < sample; i += 1) {
      node.children.forEach((leaf) => emitLeaf(leaf, sectionCode, ctx));
    }
    const hidden = node.repeatCount - sample;
    if (hidden > 0) ctx.step.n += hidden * node.children.length;
    // A repeatCount of 1 (ramp expansion, bare superset) is not a real loop — skip the row.
    if (node.repeatCount > 1) {
      emitLoopRow(`bloc ${(ctx.block.n += 1)}`, node.repeatCount, ' répétitions', ctx);
    }
  } else {
    node.children.forEach((child) => emitNode(child, false, sectionCode, ctx));
    if (node.repeatCount > 1) {
      const label = isOutermost ? 'bloc complet' : `bloc ${(ctx.block.n += 1)}`;
      emitLoopRow(label, node.repeatCount, '', ctx);
    }
  }
};

export const flattenPreview = (sections: DecodedInterval[], sportIdentifier?: string): DecodeRow[] => {
  const ctx: FlattenCtx = { rows: [], step: { n: 1 }, block: { n: 0 }, sportIdentifier };
  sections.forEach((s) => emitNode(s, s.repeatCount > 1, s.section, ctx));
  return ctx.rows;
};

const findLeafBlock = (node: DecodedInterval): DecodedInterval | null => {
  if (isGroup(node) && node.repeatCount > 1 && node.children.every((c) => c.children.length === 0)) return node;
  for (const child of node.children) {
    const found = findLeafBlock(child);
    if (found) return found;
  }
  return null;
};

export const summarize = (preview: DecodePreview, sportIdentifier?: string): string => {
  const isSwim = (sportIdentifier ?? '').trim().toUpperCase() === 'SWIMMING';
  const useDistance = isSwim && (preview.distanceM ?? 0) > 0;
  const duration = useDistance
    ? `distance totale ${formatMetres(preview.distanceM as number)}`
    : `durée totale estimée ${formatMinutes(preview.estimatedDurationSec)} hors échauffement`;
  const main = preview.sections[0];
  if (!main) return `${duration}.`;

  const outerWrapper = isGroup(main) && main.repeatCount > 1 && main.children.some((c) => c.children.length > 0);
  const leafBlock = findLeafBlock(main);

  if (outerWrapper && leafBlock) {
    return `Répétition ${main.repeatCount} × bloc de ${leafBlock.repeatCount} · ${duration}.`;
  }
  if (leafBlock) {
    return `Bloc de ${leafBlock.repeatCount} · ${duration}.`;
  }
  return `${duration}.`;
};
